import type { Selection } from '@tiptap/pm/state';
import lodash from 'lodash';
import { isPresent } from 'ts-is-present';

import type {
  EditorPlugin as EditorPluginType,
  IRicosEditor,
  PluginExports,
  PublicCommands,
  QuickAction,
  RicosServices,
  ToolbarSettings,
} from './../ricos-types';

import { EditorPlugin } from './editorPlugin';
import { mergeConfig } from './mergeConfig';
import { RicosPluginAddButtons } from './plugin-add-buttons';
import type { FormattingToolbarButtons, PluginTextButton } from './plugin-text-button';
import { PluginTextButtons } from './plugin-text-button';

export type PluginServices = Omit<
  RicosServices,
  'plugins' | 'editor' | 'zIndexService' | 'getEditorQuery'
>;

export class PluginCollisionError extends Error {}

export class EditorPlugins {
  private plugins: EditorPlugin[] = [];

  private services: PluginServices & { plugins?: EditorPlugins };

  private toolbarSettings: ToolbarSettings;

  private publicCommands?: PublicCommands;

  static mergeConfig = mergeConfig;

  constructor(services: PluginServices, toolbarSettings: ToolbarSettings) {
    this.services = services;
    this.toolbarSettings = toolbarSettings;
  }

  register(plugin: EditorPluginType) {
    const { isMobile } = this.services?.context || {};
    if (isMobile && plugin?.disableOnMobile) {
      return;
    }
    const candidate = EditorPlugin.of(plugin, { ...this.services, plugins: this });
    const duplicate = this.hasDuplicate(candidate);
    if (duplicate) {
      throw new PluginCollisionError(
        `the plugin ${candidate.getType()} conflicts with ${duplicate.getType()}`
      );
    }

    candidate.register();
    this.plugins.push(candidate);
  }

  unregister(plugin: EditorPlugin) {
    plugin.unregister();
    return this.filter(p => !p.equals(plugin));
  }

  destroy() {
    this.plugins.map(p => p.unregister());
    this.plugins = [];
  }

  filter(predicate: (plugin: EditorPlugin) => boolean) {
    this.plugins = this.plugins.filter(predicate);
    return this.plugins;
  }

  asArray() {
    return this.plugins;
  }

  getConfig(type: string) {
    return this.plugins.filter(plugin => plugin.getType() === type)[0]?.getConfig() || {};
  }

  getTextButtons(): FormattingToolbarButtons {
    const textButtons = this.plugins.flatMap(
      plugin => (plugin.getTextButtons() as PluginTextButton[]) || []
    );
    return PluginTextButtons.of(textButtons, this.services, this.toolbarSettings);
  }

  getQuickActions(): QuickAction[] {
    return this.plugins.flatMap(plugin => plugin.getQuickActions());
  }

  getAddButtons() {
    //maybe use filter class func
    const addButtons = this.plugins.flatMap(plugin => plugin.getAddButtons() || []);
    return new RicosPluginAddButtons(addButtons, this.services, this.toolbarSettings);
  }

  getFloatingToolbarButtons() {
    return this.plugins
      .flatMap(plugin => plugin.getFloatingToolbarButton())
      .filter(isPresent)
      .sort(({ priority: a }, { priority: b }) => b - a);
  }

  getExports() {
    return this.plugins
      .map(plugin => plugin.getExports())
      .filter(isPresent)
      .reduce((integrations, pluginIntegrations) => {
        return { ...integrations, ...pluginIntegrations };
      }, {} as Partial<PluginExports>);
  }

  getVisibleToolbar(selection: Selection, editor: IRicosEditor) {
    const toolbar = this.plugins
      .flatMap(plugin => plugin.getToolbars())
      .find(toolbar => !!toolbar?.isVisible(selection, editor));
    return toolbar;
  }

  getTiptapExtensions() {
    return lodash.compact(this.plugins.flatMap(plugin => plugin.getTiptapExtensions?.() || []));
  }

  isFooterToolbarDisabled(selection: $TSFixMe) {
    const nodeType = selection?.node.type.name;
    if (nodeType) {
      const selectedPlugin = this.plugins.find(
        plugin => plugin?.getTiptapExtensions()?.[0]?.name === nodeType
      );
      return !!selectedPlugin?.getTiptapExtensions()?.[0]?.groups.includes('footer-disabled');
    }
    return false;
  }

  private hasDuplicate(plugin: EditorPlugin) {
    return this.plugins.find(p => p.equals(plugin));
  }

  setPublicCommands(publicCommands: PublicCommands) {
    this.publicCommands = publicCommands;
  }

  getPublicCommands() {
    return this.publicCommands;
  }
}
